/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Cross-module service: bridges Menu Management → Aggregator Operations.
 * Fetches menu profiles via the generic data repository so the Aggregator
 * module never imports Menu-Manage components directly.
 */

import { getRepository } from '../../../config/repository';
import type { User } from '../../../domain/entities';
import type { MenuProfile, MenuItem, OptionGroup } from '../../menu-manage/types';
import type { SubmissionItem, ImportedOptionGroup } from '../types';

// ─── Public API ─────────────────────────────────────────────────────────────────

/** Check whether the given user has access to the menu-manage module. */
export function hasMenuAccess(user: User | null): boolean {
  if (!user) return false;
  // Admins / Super-Admins have unrestricted access when no explicit list
  if (!user.assignedModules) return true;
  return user.assignedModules.includes('menu-manage' as any);
}

/** Fetch menu profiles that belong to the brands the user is assigned to. */
export async function getMenuProfilesForUser(
  user: User | null,
  brandIds?: string[],
): Promise<MenuProfile[]> {
  if (!user) return [];
  const repo = getRepository();
  const allProfiles = await repo.getAll<MenuProfile>('menu-profiles');

  // Brand-lock: only profiles matching user's assigned brands (or all if unrestricted)
  const allowedBrands = brandIds ?? user.assignedBrands;
  if (!allowedBrands || allowedBrands.length === 0) return allProfiles;
  return allProfiles.filter(p => allowedBrands.includes(p.brandId));
}

/** Map a Menu Management MenuItem into an Aggregator SubmissionItem. */
export function mapMenuItemToSubmissionItem(
  menuItem: MenuItem,
  profile: MenuProfile,
): SubmissionItem {
  // Map option groups
  const importedOptions: ImportedOptionGroup[] = (menuItem.optionGroups ?? [])
    .filter(g => !g.hidden)
    .map(mapOptionGroup);

  return {
    id: crypto.randomUUID(),
    name: menuItem.nameEN || menuItem.nameAR,
    nameAR: menuItem.nameAR || '',
    price: menuItem.basePrice,
    description: menuItem.descriptionEN || menuItem.descriptionAR || '',
    descriptionAR: menuItem.descriptionAR || '',
    imageUrl: menuItem.image ?? '',
    // Menu-link metadata
    menuLinked: true,
    menuProfileId: profile.id,
    menuProfileName: profile.name,
    menuItemId: menuItem.id,
    originalMenuPrice: menuItem.basePrice,
    importedOptions,
  };
}

// ─── Internal helpers ───────────────────────────────────────────────────────────

function mapOptionGroup(group: OptionGroup): ImportedOptionGroup {
  return {
    id: group.id,
    nameEN: group.nameEN,
    nameAR: group.nameAR,
    minSelection: group.minSelection,
    maxSelection: group.maxSelection,
    options: group.options
      .filter(o => !o.hidden)
      .map(o => ({
        id: o.id,
        nameEN: o.nameEN,
        nameAR: o.nameAR,
        extraPrice: o.extraPrice,
      })),
  };
}
